/**
 * 会话控制器（聊天页的全部状态机）。
 *
 * 这里是整个前端最容易写错的地方，所以把"为什么"逐条写下来。
 *
 * 1. **乐观插入**。用户按下回车后立刻把消息插进列表，不等服务端。`meta` 帧回来时
 *    再把服务端 id 补到同一条上（靠 `key` 对齐，而不是靠数组下标）。
 * 2. **delta 节流**。上游可能每 10ms 吐一个 token，每个 token 都改一次响应式状态会让
 *    整个气泡重渲染（Markdown 重新解析）。40ms 一次的合并写入把渲染次数压到 25/s，
 *    肉眼看仍然是"逐字出现"。
 * 3. **两代令牌防串台**。`loadTokenRef` 防"历史加载被后来的加载覆盖"，
 *    `resetGenerationRef` 防"上一轮会话的流式回调把内容画进新会话"。两者缺一不可：
 *    前者管读接口，后者管流式写回调。
 * 4. **abort 先于清空**。切会话时必须先 abort 再清状态，否则被中断的那一轮会在
 *    catch 分支里把"已中断"画到**新**会话的气泡上。
 */

import { computed, onScopeDispose, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';

import {
  ErrorCode,
  MessagePageSchema,
  type Message,
  type MessagePage,
} from '@spirit/contracts';

import type { AgeBand, Domain } from '@spirit/contracts';

import { apiRequest, isApiError, isAbortError } from './api-client';
import { emitApiEvent, type QuotaExceededDetails } from './api-events';
import { applyQuota, useAuth } from './auth-store';
import { streamChatCompletion } from './chat-stream';
import { createAssistantDraft, messageToViewModel } from '../types';
import type { AssistantDraft, ChatListItem, LocalUserMessage } from '../types';

/** 历史消息每页条数。 */
const HISTORY_PAGE_LIMIT = 50;
/**
 * 历史消息最多翻几页（50 × 4 = 200 条）。
 *
 * 消息游标是**升序**的，越翻越新；不设上限的话，一个几千条的会话会让用户
 * 在"打开会话"上花掉好几秒和几十个请求，而他只想看最后几句话。
 */
const HISTORY_MAX_PAGES = 4;
/** 流式内容的写入节流窗口（毫秒）。 */
const DELTA_FLUSH_INTERVAL_MS = 40;

/**
 * 把 42900 的 SSE error 帧补成 `QuotaExceededDetails`。
 *
 * SSE 的 error 帧只带 `{code, message, safety}`——**没有** details 字段（见契约
 * `SseErrorPayloadSchema`），所以这里拿不到服务端随错误一起算好的 percent/resetAt。
 * 但额度条与额度页显示的就是 `auth-store` 里这一份快照，直接把它带进弹窗，
 * 屏幕上两处说的就是同一个维度、同一个百分比、同一个重置时刻。
 *
 * v1.3.0 之前这里要**反推**维度（先日、后月，外加哨兵与"月度先耗尽"两个特例），
 * 那段逻辑连同它的坑一起删掉了：既然服务端已经在快照里给出 `scope`，
 * 客户端再推一次就只是在制造第三份真相。
 *
 * 快照缺失（冷启动直接开口就撞额度）时只报一个空壳：`resetAt` 留空串让弹窗
 * 省略时间那一句，`scope` 退回 `daily`——不知道具体维度时不能编一个重置时间，
 * 否则用户会按错误的时间等。
 */
function quotaExceededFromSnapshot(message: string): { type: 'quota-exceeded'; message: string } & QuotaExceededDetails {
  const quota = useAuth().quota;
  if (quota === null) {
    return { type: 'quota-exceeded', message, scope: 'daily', remainingPercent: 0, resetAt: '' };
  }
  return {
    type: 'quota-exceeded',
    message,
    scope: quota.scope,
    remainingPercent: quota.remainingPercent,
    resetAt: quota.resetAt,
  };
}

export interface UseChatControllerOptions {
  /**
   * 路由里的会话 id；`null`/`undefined` 表示"新会话"。
   *
   * 三个"初值"参数都接受 `MaybeRefOrGetter`：调用方天然只有一个来源——组件的
   * `props`（`toRef(props, 'conversationId')`），而 props 是响应式的。
   * 收成普通值就必须在调用方 `watch` 之后再转交，等于把响应式边界推给每个调用方。
   */
  conversationId?: MaybeRefOrGetter<string | null | undefined>;
  /** `/chat?domain=xxx` 带过来的领域初值。 */
  domain?: MaybeRefOrGetter<Domain | null | undefined>;
  /** 年龄段初值（领域为家庭教育时才会上行）。 */
  ageBand?: MaybeRefOrGetter<AgeBand | null | undefined>;
  /** 首次收到 `meta` 帧（新会话已创建）时回调，用于同步 URL 与侧边列表。 */
  onConversationCreated?: (conversationId: string) => void;
}

export interface ChatController {
  items: ChatListItem[];
  conversationId: string | null;
  sending: boolean;
  loadingHistory: boolean;
  historyError: string | null;
  hasContent: boolean;
  canRegenerate: boolean;
  send: (text: string) => Promise<void>;
  stop: () => void;
  regenerate: () => Promise<void>;
  reset: () => void;
}

export function useChatController(options: UseChatControllerOptions): ChatController {
  const conversationIdRef = ref<string | null>(toValue(options.conversationId) ?? null);
  const domainRef = ref<Domain | null>(toValue(options.domain) ?? null);
  const ageBandRef = ref<AgeBand | null>(toValue(options.ageBand) ?? null);

  const messages = ref<Message[]>([]);
  const localUser = ref<LocalUserMessage | null>(null);
  const draftRef = ref<AssistantDraft | null>(null);

  const sendingRef = ref(false);
  const loadingHistory = ref(false);
  const historyError = ref<string | null>(null);
  const canRegenerate = ref(false);
  /** 是否已经处理过当前路由 id 的历史加载。 */
  const handledRouteIdRef = ref<string | null | undefined>(undefined);

  const abortRef = ref<AbortController | null>(null);
  let lastUserText: string | null = null;
  let localSeq = 0;
  let loadToken = 0;
  let resetGeneration = 0;

  /** 流式增量缓冲与节流计时器（刻意**不做成响应式**：它们只在快照那一刻有意义）。 */
  let buffer = '';
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  let lastFlushAt = 0;

  function cancelFlush(): void {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  }

  function applyBuffer(): void {
    if (buffer.length === 0) {
      return;
    }
    const draft = draftRef.value;
    if (draft !== null) {
      draft.content += buffer;
      draft.streaming = true;
    }
    buffer = '';
    lastFlushAt = Date.now();
  }

  /** 把缓冲写进草稿；`immediate` 用于本轮结束（done/error/abort）时的收尾。 */
  function flush(immediate: boolean): void {
    if (buffer.length === 0) {
      return;
    }
    if (immediate || Date.now() - lastFlushAt >= DELTA_FLUSH_INTERVAL_MS) {
      cancelFlush();
      applyBuffer();
      return;
    }
    if (flushTimer === null) {
      flushTimer = setTimeout(() => {
        flushTimer = null;
        applyBuffer();
      }, DELTA_FLUSH_INTERVAL_MS);
    }
  }

  /**
   * 切到目标会话：先放弃旧世界，再建立新世界。
   *
   * 顺序不能换。abort 会触发上一轮的 catch，而 catch 里要写 `draftRef`；
   * 如果先清空再 abort，那个 catch 就会把"已中断"写到**新**会话的气泡上。
   */
  function resetForConversation(target: string | null): void {
    loadToken += 1;
    resetGeneration += 1;

    if (abortRef.value !== null) {
      abortRef.value.abort();
      abortRef.value = null;
    }
    cancelFlush();
    buffer = '';

    conversationIdRef.value = target;
    messages.value = [];
    localUser.value = null;
    draftRef.value = null;
    historyError.value = null;
    canRegenerate.value = false;
    sendingRef.value = false;
    lastFlushAt = 0;
  }

  function patchLocalUser(patch: Partial<LocalUserMessage>): void {
    localUser.value = localUser.value === null ? null : { ...localUser.value, ...patch };
  }

  function patchDraft(patch: Partial<AssistantDraft>): void {
    draftRef.value = draftRef.value === null ? null : { ...draftRef.value, ...patch };
  }

  async function loadHistory(target: string): Promise<void> {
    const token = loadToken;
    const generation = resetGeneration;

    loadingHistory.value = true;
    historyError.value = null;

    try {
      const collected: Message[] = [];
      let cursor: string | null = null;

      for (let page = 0; page < HISTORY_MAX_PAGES; page += 1) {
        // 显式标注返回类型：`ParseableSchema<T>` 的 T 只能从 zod 的 `safeParse`
        // 反推，配合循环里对 `cursor` 的赋值会让编译器放弃推断（TS7022）。
        const result: MessagePage = await apiRequest(
          `/conversations/${encodeURIComponent(target)}/messages`,
          MessagePageSchema,
          {
            method: 'GET',
            query: { limit: HISTORY_PAGE_LIMIT, ...(cursor === null ? {} : { cursor }) },
          },
        );

        // 每次 await 之后都必须重新验票：用户可能已经切走了。
        if (token !== loadToken || generation !== resetGeneration) {
          return;
        }

        collected.push(...result.items);
        cursor = result.nextCursor;
        if (cursor === null) {
          break;
        }
      }

      if (token !== loadToken || generation !== resetGeneration) {
        return;
      }

      // 游标是升序的，但服务端不保证同一毫秒内的顺序稳定；渲染前再按时间排一次，
      // 否则会出现"回答排在提问前面"这种看起来像数据损坏的画面。
      collected.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      messages.value = collected;
    } catch (error) {
      if (token !== loadToken || generation !== resetGeneration) {
        return;
      }
      historyError.value = isApiError(error) ? error.message : '会话内容加载失败，请稍后重试';
    } finally {
      if (token === loadToken && generation === resetGeneration) {
        loadingHistory.value = false;
      }
    }
  }

  /**
   * 历史加载的唯一入口。
   *
   * 判据是"**这个路由 id 我处理过了吗**"，而不是"它是否等于当前会话 id"。
   * 后者在 /chat/<id> 这种"路由先到、会话后建"的场景里恒为真，
   * 结果是历史永远不加载（页面永远空白，像坏了一样）。
   * 也不能用"URL 是不是我们自己改的"这种标志位：那个标志会在真实切换会话时
   * 残留下来，把用户的新会话整个吞掉。
   */
  watch(
    // `toValue` 而不是直接读 `options.conversationId`：这里必须建立依赖，
    // 否则 watch 只在 immediate 那一次跑过，之后**永远不再触发**——
    // 从会话列表点进另一个会话时，历史会静默地不加载。
    () => toValue(options.conversationId) ?? null,
    (target) => {
      if (handledRouteIdRef.value === target) {
        return;
      }
      handledRouteIdRef.value = target;
      resetForConversation(target);
      if (target === null) {
        return;
      }
      void loadHistory(target);
    },
    { immediate: true },
  );

  /** 一个完整的问答回合。`send` 与 `regenerate` 都走这里。 */
  async function runTurn(text: string): Promise<void> {
    const trimmed = text.trim();
    if (trimmed.length === 0 || sendingRef.value) {
      return;
    }

    sendingRef.value = true;
    canRegenerate.value = false;
    historyError.value = null;
    lastUserText = trimmed;

    localSeq += 1;
    localUser.value = {
      key: `local-user-${localSeq}`,
      id: null,
      content: trimmed,
      domain: domainRef.value,
      // 年龄段只在家庭教育领域上行：别的领域带一个年龄会误导服务端的路由。
      ageBand: domainRef.value === 'family_education' ? ageBandRef.value : null,
    };

    buffer = '';
    lastFlushAt = Date.now();
    cancelFlush();
    draftRef.value = createAssistantDraft();

    const controller = new AbortController();
    abortRef.value = controller;
    const generation = resetGeneration;
    let conversationMissing = conversationIdRef.value === null;

    /** 流式回调可能在切会话之后才到达，此时一切写入都必须丢弃。 */
    function stale(): boolean {
      return generation !== resetGeneration;
    }

    try {
      await streamChatCompletion({
        input: {
          ...(conversationIdRef.value === null ? {} : { conversationId: conversationIdRef.value }),
          message: trimmed,
          ...(domainRef.value === null ? {} : { domain: domainRef.value }),
          ...(ageBandRef.value === null ? {} : { ageBand: ageBandRef.value }),
        },
        signal: controller.signal,
        callbacks: {
          onMeta: (payload) => {
            if (stale()) {
              return;
            }
            patchLocalUser({
              id: payload.userMessageId,
              domain: payload.domain,
              ageBand: payload.ageBand,
            });
            patchDraft({
              id: payload.assistantMessageId,
              domain: payload.domain,
              ageBand: payload.ageBand,
              upstreamModel: payload.upstreamModel,
              createdAt: new Date().toISOString(),
              streaming: true,
            });

            if (conversationMissing) {
              conversationMissing = false;
              conversationIdRef.value = payload.conversationId;
              options.onConversationCreated?.(payload.conversationId);
            }
          },
          onDelta: (payload) => {
            if (stale()) {
              return;
            }
            buffer += payload.content;
            flush(false);
          },
          onUsage: (payload) => {
            if (stale()) {
              return;
            }
            // 额度快照照旧当场刷新（额度条要立刻动），但**没有**任何 token 量可写进气泡：
            // 帧里只剩 `estimated`，界面据此决定要不要打「估算」标。
            applyQuota(payload.quota);
            patchDraft({ estimated: payload.estimated });
          },
          onRetract: (payload) => {
            if (stale()) {
              return;
            }
            // 顺序不能换：先把缓冲里的最后几个字冲进正文，再整条清掉。
            // 反过来的话，`flush` 会把已经"撤回"的正文又写回草稿里。
            flush(true);
            patchDraft({
              id: payload.messageId,
              // 正文一律清空：撤回的意思就是"这段回答不该留在用户面前"。
              // 只留一张安全提示卡（publicMessage），判定依据不进界面。
              content: '',
              retracted: true,
              streaming: false,
              errorMessage: null,
              safetyMessage: payload.safety.publicMessage,
            });
            // 与输入侧拦截不同（那种情况重试必然再次被拦，所以不给重试），
            // 这里被撤回的是**模型这一次的措辞**，用户换个问法或重试是合理的。
            canRegenerate.value = true;
          },
          onDone: (payload) => {
            if (stale()) {
              return;
            }
            flush(true);
            patchDraft({
              id: payload.messageId,
              streaming: false,
              latencyMs: payload.latencyMs,
              finishReason: payload.finishReason,
            });
          },
          onError: (payload) => {
            if (stale()) {
              return;
            }
            flush(true);
            patchDraft({
              streaming: false,
              errorMessage: payload.message,
              safetyMessage: payload.safety?.publicMessage ?? null,
            });

            // 安全拦截的处置是"就地说明"，不是"弹窗催促重试"。这里必须提前返回，
            // 否则用户会同时看到一个红色安全提示和一个催促他再试一次的弹窗。
            if (payload.safety !== undefined) {
              canRegenerate.value = false;
              return;
            }

            switch (payload.code) {
              case ErrorCode.QUOTA_EXCEEDED:
                emitApiEvent(quotaExceededFromSnapshot(payload.message));
                break;
              case ErrorCode.RATE_LIMITED:
                emitApiEvent({ type: 'rate-limited', message: payload.message, retryAfterSeconds: 5 });
                break;
              case ErrorCode.UPSTREAM_ERROR:
              case ErrorCode.UPSTREAM_TIMEOUT:
                emitApiEvent({ type: 'upstream-failure', message: payload.message, notBillable: true });
                break;
              case ErrorCode.UNAUTHORIZED:
              case ErrorCode.TOKEN_EXPIRED:
                emitApiEvent({ type: 'unauthorized', message: payload.message });
                break;
              default:
                emitApiEvent({ type: 'upstream-failure', message: payload.message, notBillable: false });
                break;
            }
            canRegenerate.value = true;
          },
        },
      });
    } catch (error) {
      if (stale()) {
        return;
      }
      flush(true);

      if (isAbortError(error)) {
        patchDraft({ streaming: false, aborted: true });
        canRegenerate.value = true;
      } else {
        const message = isApiError(error) ? error.message : '发送失败，请检查网络后重试';
        const current = draftRef.value;
        if (current === null) {
          draftRef.value = { ...createAssistantDraft(), streaming: false, errorMessage: message };
        } else if (typeof current.safetyMessage === 'string' && current.safetyMessage.length > 0) {
          // 安全提示已经占了正文位置，再补一条黄色错误条会把同一件事说两遍。
          patchDraft({ streaming: false });
        } else {
          patchDraft({ streaming: false, errorMessage: message });
        }
        canRegenerate.value = !(isApiError(error) && error.code === ErrorCode.RISK_BLOCKED);
      }
    } finally {
      if (!stale()) {
        abortRef.value = null;
        sendingRef.value = false;
        flush(true);
        // 兜底：即使 onDone/onError 都没被调用（例如服务端发了一个 schema 合法但
        // 业务上被拒的帧），也必须把骨架屏关掉，不能让用户盯着一个永远转的圈。
        patchDraft({ streaming: false });
      }
    }
  }

  function send(text: string): Promise<void> {
    return runTurn(text);
  }

  function stop(): void {
    abortRef.value?.abort();
  }

  /**
   * 重新生成。
   *
   * 契约里**没有** regenerate 端点，所以只能把上一条用户消息重发一次。
   * 已知代价：如果 `meta` 帧已经到达（说明服务端已经落库），重发会在服务端
   * 再产生一条用户消息——这是"没有端点"这件事的必然结果，不是 bug。
   */
  async function regenerate(): Promise<void> {
    const text = lastUserText;
    if (text === null || sendingRef.value) {
      return;
    }
    draftRef.value = null;
    localUser.value = null;
    await runTurn(text);
  }

  /** 开一个新会话（"新对话"按钮、以及聊天页的 reset 事件）。 */
  function reset(): void {
    // 顺序有意为之：先清 id 再清状态。反过来的话，reset 期间若触发一次发送，
    // 就会把消息发进上一个会话里。
    conversationIdRef.value = null;
    lastUserText = null;
    resetForConversation(null);
  }

  const items = computed<ChatListItem[]>(() => {
    const list: ChatListItem[] = messages.value.map((message) => ({
      kind: 'message' as const,
      key: message.id,
      view: messageToViewModel(message),
    }));
    const pending = localUser.value;
    if (pending !== null) {
      list.push({ kind: 'local-user', key: pending.key, local: pending });
    }
    const draft = draftRef.value;
    if (draft !== null) {
      list.push({ kind: 'assistant', key: 'assistant-draft', draft });
    }
    return list;
  });

  const hasContent = computed<boolean>(() => items.value.length > 0);

  onScopeDispose(() => {
    cancelFlush();
    if (abortRef.value !== null) {
      abortRef.value.abort();
      abortRef.value = null;
    }
  });

  return {
    // 必须是 getter。写成 `items: items.value` 会把 computed 当场求值一次，
    // 于是控制器永远返回「挂载那一刻」的空数组——发消息、收流式帧、加载历史
    // 全都照常进行，界面上却一个字都不出现（消息区永远停在引导语上）。
    get items() {
      return items.value;
    },
    get conversationId() {
      return conversationIdRef.value;
    },
    get sending() {
      return sendingRef.value;
    },
    get loadingHistory() {
      return loadingHistory.value;
    },
    get historyError() {
      return historyError.value;
    },
    get hasContent() {
      return hasContent.value;
    },
    get canRegenerate() {
      return canRegenerate.value;
    },
    send,
    stop,
    regenerate,
    reset,
  };
}

/** 便于视图层在需要时按需读取内部的 ref（例如把 domain 变化同步回 URL）。 */
export type ChatControllerRefs = {
  conversationId: Ref<string | null>;
};
